import { MathUtils, Mesh, type Material } from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";

import type { SlotLook } from "../../render/materials/slotLook";
import { ORIGINAL_LOOK } from "../../render/renderConstants";
import { materialForSlot } from "../../render/materials/slotMaterial";
import { AnimatedModel } from "../../render/models/AnimatedModel";
import { nameMaterialSlots, readModelInfo, type ModelInfo } from "../model/modelInfo";

export class ViewerModel extends AnimatedModel {
  readonly info: ModelInfo;
  private readonly slotNames: Map<Material, string>;
  private readonly exportedMaterials = new Map<Mesh, Material | Material[]>();
  private overrides = new Map<Material, Material>();

  constructor(gltf: GLTF) {
    super(gltf);
    this.info = readModelInfo(gltf.scene, gltf.animations);
    this.slotNames = nameMaterialSlots(gltf.scene);

    gltf.scene.traverse((object) => {
      if (!(object instanceof Mesh)) return;
      this.exportedMaterials.set(object, object.material);
    });
  }

  setTransform(scale: number, rotationYDegrees: number) {
    this.root.scale.setScalar(scale);
    this.root.rotation.y = MathUtils.degToRad(rotationYDegrees);
  }

  setMaterials(slotLooks: Record<string, SlotLook>) {
    const previousOverrides = this.overrides;
    this.overrides = new Map();
    for (const [material, slotName] of this.slotNames) {
      this.overrides.set(material, materialForSlot(material, slotLooks[slotName] ?? ORIGINAL_LOOK));
    }
    this.useOverrides();
    disposeOverrides(previousOverrides);
  }

  dispose() {
    this.setMaterials({});
    super.dispose();
  }

  private useOverrides() {
    for (const [mesh, exported] of this.exportedMaterials) {
      mesh.material = Array.isArray(exported)
        ? exported.map((material) => this.overrides.get(material) ?? material)
        : (this.overrides.get(exported) ?? exported);
    }
  }
}

function disposeOverrides(overrides: Map<Material, Material>) {
  for (const [exported, override] of overrides) {
    if (override !== exported) override.dispose();
  }
}
